import type { Annotation } from "@/lib/api";

import type { AnnotationDraft } from "./fabric-editor";

/** The editing session's authoritative ink collection (plan north-star C).
 *
 * A per-document, framework-free model that OWNS the ink while a document is
 * open — NOT the React-Query cache. Every editor seeds from it, every local
 * mutation lands on it synchronously, and a background refetch can only ADD what
 * the session has never seen or refresh untouched entries; it can never remove
 * or blank locally-known ink. That single invariant is what structurally kills
 * the seed-race (#5b): there is no longer a stale snapshot for a rebuilt editor
 * to seed from.
 *
 * On top of the collection sit two things the old per-editor world couldn't
 * have: a session undo/redo stack, and a batched, park-and-replay persistence
 * queue that survives editor teardown (it lives with the model, which ReaderTab
 * owns for the whole document, not with the disposable PageEditor).
 *
 * Mutations flow through {@link transact}: ops apply to local state at once, ONE
 * undo entry is captured (with inverse payloads from the pre-state), and ONE
 * batch write is enqueued. Creates return a temp id (`tmp-<uuid>`, see
 * {@link nextTempId}) immediately so
 * the editor can tag its Fabric object without waiting on the network; the flush
 * turns temp ids into `ref`s and maps the server's ids back (emitting
 * `idChanged`, rewriting any still-queued ops and the undo stacks). */

// ---- wire shapes (mirror server domain.annotations MarkupBatch*) ------------

/** A create in a batch, carrying the client temp id as `ref`. */
export type MarkupBatchCreate = AnnotationDraft & { ref: string };
/** A partial update in a batch, targeting an existing annotation by id. */
export type MarkupBatchUpdate = Partial<AnnotationDraft> & { id: string };

export interface MarkupBatchRequest {
  creates: MarkupBatchCreate[];
  updates: MarkupBatchUpdate[];
  deletes: string[];
}

export interface MarkupBatchResult {
  created: { ref: string; annotation: Annotation }[];
  updated: Annotation[];
  deleted: string[];
}

// ---- events -----------------------------------------------------------------

/** The model's typed event surface. Viewers subscribe and route each event to
 * the owning page editor (edit mode) or a read-only overlay redraw (reading). */
export interface InkModelEventMap {
  /** An annotation entered the model (reconcile of an unseen row, or an undo/redo
   * that re-created a deleted one). */
  added: [a: Annotation];
  /** An annotation left the model (undo/redo of a create). */
  removed: [id: string, page: number];
  /** An annotation's fields changed (undo/redo of an edit, reconcile of an
   * untouched row). */
  replaced: [a: Annotation];
  /** A temp id resolved to its server id after a flush — the editor rewrites the
   * matching object's annId so later edits/erases target the real row. */
  idChanged: [tempId: string, serverId: string, page: number];
  /** canUndo/canRedo may have changed — the toolbar re-reads them. */
  undoState: [];
}

type EventKey = keyof InkModelEventMap;
type Handler<K extends EventKey> = (...args: InkModelEventMap[K]) => void;

// ---- pure helpers (exported for tests) --------------------------------------

/** A provisional client id, minted on create and swapped for the server id on
 * flush. The `tmp-` prefix is load-bearing: the persistence queue never sends a
 * temp id to the server (it can't resolve one) — it waits for the id to resolve. */
export function isTempId(id: string): boolean {
  return id.startsWith("tmp-");
}

/** The annotation fields the editor can mutate; page + type are immutable (a
 * server constraint — delete + recreate to change them). Used to shape both an
 * undo before/after patch and a batch update. */
const MUTABLE_FIELDS = [
  "points",
  "text",
  "color",
  "font_family",
  "font_size_rel",
  "stroke_rel",
  "opacity",
  "rect",
  "rings",
  "cols",
  "rows",
  "fill_color",
  "dash",
  "corner_radius",
] as const;

/** A deep-enough clone: nested points/rect are copied so an undo snapshot (or a
 * seeded row) can never be mutated in place by a later edit of the live copy. */
export function cloneAnnotation(a: Annotation): Annotation {
  return {
    ...a,
    points: a.points ? a.points.map((p) => ({ ...p })) : a.points,
    rect: a.rect ? { ...a.rect } : a.rect,
    rings: a.rings ? a.rings.map((ring) => ring.map((p) => ({ ...p }))) : a.rings,
    cols: a.cols ? [...a.cols] : a.cols,
    rows: a.rows ? [...a.rows] : a.rows,
  };
}

/** The AnnotationCreate payload (page + type + defined mutable fields) for an
 * annotation — the create half of a batch. */
export function annotationToCreate(a: Annotation): AnnotationDraft {
  const out: AnnotationDraft = { page: a.page, type: a.type };
  copyMutable(a, out);
  return out;
}

/** The AnnotationUpdate payload (defined mutable fields only) for an annotation —
 * the update half of a batch. Only DEFINED fields are sent: the server pops a key
 * set to null, so omitting an absent field leaves it untouched. */
export function annotationToUpdate(a: Annotation): Partial<AnnotationDraft> {
  const out: Partial<AnnotationDraft> = {};
  copyMutable(a, out);
  return out;
}

function copyMutable(a: Annotation, out: Partial<AnnotationDraft>): void {
  for (const k of MUTABLE_FIELDS) {
    const v = (a as unknown as Record<string, unknown>)[k];
    if (v == null) continue;
    if (k === "points" && a.points) {
      out.points = a.points.map((p) => {
        const q: { x: number; y: number; pressure?: number } = { x: p.x, y: p.y };
        if (p.pressure != null) q.pressure = p.pressure;
        return q;
      });
    } else {
      (out as Record<string, unknown>)[k] = v;
    }
  }
}

// ---- undo entry -------------------------------------------------------------

/** One reversible MARKUP transaction (create/update/delete of annotations). Ids
 * inside are kept CURRENT: {@link InkModel.reId} rewrites them when a temp id
 * resolves, and undo/redo rewrite the re-created births/deaths to their fresh temp
 * ids, so the opposite operation always targets the annotation that is actually
 * live. */
interface MarkupUndoEntry {
  kind: "markup";
  /** Annotations the transaction created (snapshots). Undo deletes them; redo
   * re-creates them with fresh ids. */
  creates: Annotation[];
  /** Field-level before/after of edited annotations. */
  updates: { id: string; before: Partial<AnnotationDraft>; after: Partial<AnnotationDraft> }[];
  /** Annotations the transaction deleted (snapshots). Undo re-creates them with
   * fresh ids; redo deletes them again. */
  deletes: Annotation[];
}

/** A self-contained COMMAND on the undo stack (page add/remove — plan F9). Unlike a
 * markup entry it is NOT a batchable set of annotation ops: its undo/redo perform
 * their OWN work (a page endpoint call + the matching local renumber + re-creating a
 * removed page's ink), so the model just sequences the closures into the shared
 * stack. Two things fall out of this design and are the reason it needs no id-reId
 * or page-rebasing bookkeeping:
 *  - captured ink is always RE-CREATED with fresh ids on undo, so a command never
 *    references a live id that a flush could rewrite; and
 *  - strict LIFO means every markup entry is reverted/re-applied in the exact page
 *    structure it was captured in — any page op above it in a stack is undone/redone
 *    first — so a frozen snapshot's `page` is never used in a shifted world. */
interface CommandUndoEntry {
  kind: "command";
  undo: () => void | Promise<void>;
  redo: () => void | Promise<void>;
}

type AnyUndoEntry = MarkupUndoEntry | CommandUndoEntry;

const UNDO_DEPTH = 100;

/** The op set {@link InkModel.transact} accepts. */
export interface TxOps {
  creates?: AnnotationDraft[];
  updates?: { id: string; patch: Partial<AnnotationDraft> }[];
  deletes?: string[];
}

/** The resolved op set the internal apply primitive works on (creates carry full
 * annotations, ids already assigned). */
interface ResolvedOps {
  creates: Annotation[];
  updates: { id: string; patch: Partial<AnnotationDraft> }[];
  deletes: string[];
}

/** The ids a built batch actually carried — so a failed send restores exactly
 * them (and no more) to the dirty sets. */
interface Consumed {
  creates: string[];
  updates: string[];
  deletes: string[];
}

export interface InkModelOptions {
  docId: string;
  /** Sends ONE batch and resolves the server result. The caller wires this to
   * `api.request("POST", …/markup/batch)` (through the house api-client, with its
   * transient-retry wrapper), so this module stays free of fetch + auth. */
  sendBatch: (req: MarkupBatchRequest) => Promise<MarkupBatchResult>;
  /** A write was parked for later replay (offline / signed out) — the UI reassures
   * the user their markup is held and will sync. */
  onPersistTrouble?: () => void;
  /** The batch was DEFINITIVELY rejected (a non-transient 4xx), so replaying the
   * same payload can only fail again. The UI tells the user honestly (their marks
   * won't sync) instead of the "held, will sync" reassurance. The rejecting error
   * is passed through: without it "the server rejected it" is indistinguishable
   * from a real client bug — a studio talking to an OLDER API rejects every
   * annotation type the API predates, and only the server's own message says so. */
  onPersistReject?: (error: unknown) => void;
  /** Classify a `sendBatch` error: `true` = transient (network / cold-auth / 5xx),
   * park + replay; `false` = definitive, drop the batch so ONE poison op can't
   * wedge every future write behind an atomic batch. Defaults to "all transient"
   * (unconditional park + replay) so a caller that doesn't classify never drops. */
  isTransient?: (e: unknown) => boolean;
  /** Debounce before a flush coalesces the tick's ops into one batch (ms). */
  flushDelayMs?: number;
  /** Retry cadence for a parked batch, alongside the `online` listener (ms). */
  replayDelayMs?: number;
  /** Max operations per outgoing batch. A flush sends at most this many ops and
   * reschedules the remainder, so a long session's accumulated queue NEVER goes
   * out as one over-limit request. The server rejects a batch above its own op
   * cap with a definitive 4xx (422), which — pre-fix — the model DROPPED, losing
   * every stroke in it (Bug A: the ~20% tail that piled up while parked). Kept
   * safely BELOW the server cap (500) so proactive chunking means that rejection
   * simply never happens. Defaults to 400. */
  maxBatchOps?: number;
  /** Same-origin URL of the batch endpoint for the pagehide beacon. A dying page
   * aborts a normal async flush, so {@link flushBeacon} fires a `navigator.sendBeacon`
   * POST here instead (the id-token cookie rides along — no header needed). Omitted
   * in unit tests that don't exercise the beacon. */
  beaconUrl?: string;
}

export class InkModel {
  private readonly docId: string;
  private readonly sendBatch: (req: MarkupBatchRequest) => Promise<MarkupBatchResult>;
  private readonly onPersistTrouble?: () => void;
  private readonly onPersistReject?: (error: unknown) => void;
  private readonly isTransient: (e: unknown) => boolean;
  private readonly flushDelayMs: number;
  private readonly replayDelayMs: number;
  private readonly beaconUrl?: string;
  private readonly maxBatchOps: number;
  // The op ceiling for the NEXT send. Normally === maxBatchOps; a definitive
  // rejection of a multi-op batch halves it (bisection — see flush) to isolate a
  // genuine poison op from its innocent neighbours, and any success re-opens it.
  private chunkLimit: number;

  // The authoritative collection + a per-page index for editor seeding.
  private readonly map = new Map<string, Annotation>();
  private readonly pages = new Map<number, Set<string>>();
  private seeded = false;

  // Ids the session has created/updated/deleted — reconcile must not clobber
  // these with server truth (the local edit wins for the session's lifetime).
  private readonly sessionTouched = new Set<string>();
  // Server ids the session deleted — reconcile must not resurrect them.
  private readonly tombstones = new Set<string>();

  // The persistence queue: dirty id sets. The flush REBUILDS each op from current
  // model state, so multiple edits to one id coalesce and a since-deleted create
  // simply drops (the live guard) — no stale payloads to reconcile.
  private readonly dirtyCreates = new Set<string>();
  private readonly dirtyUpdates = new Set<string>();
  private readonly dirtyDeletes = new Set<string>();

  private tempSeq = 0;
  private undoStack: AnyUndoEntry[] = [];
  private redoStack: AnyUndoEntry[] = [];

  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private replayTimer: ReturnType<typeof setTimeout> | null = null;
  private flushing = false;
  // The promise of the send currently awaiting the network (null when idle). A
  // page op awaits this via {@link settle} so no create carrying a pre-shift page
  // is still in flight when a shift endpoint fires.
  private inFlight: Promise<void> | null = null;
  private parked = false;
  private disposed = false;

  private readonly listeners: { [K in EventKey]: Set<Handler<K>> } = {
    added: new Set(),
    removed: new Set(),
    replaced: new Set(),
    idChanged: new Set(),
    undoState: new Set(),
  };

  private readonly onOnline = () => {
    if (this.parked) void this.flush();
  };
  // A closing/frozen page aborts a normal async flush mid-request, dropping the
  // last ~flush-cycle of strokes. A beacon is delivered by the browser even after
  // the page is gone, so pagehide drains through it instead. (See flushBeacon.)
  private readonly onPageHide = () => {
    this.flushBeacon();
  };

  constructor(opts: InkModelOptions) {
    this.docId = opts.docId;
    this.sendBatch = opts.sendBatch;
    this.onPersistTrouble = opts.onPersistTrouble;
    this.onPersistReject = opts.onPersistReject;
    this.isTransient = opts.isTransient ?? (() => true);
    this.flushDelayMs = opts.flushDelayMs ?? 250;
    this.replayDelayMs = opts.replayDelayMs ?? 5000;
    this.beaconUrl = opts.beaconUrl;
    this.maxBatchOps = Math.max(1, opts.maxBatchOps ?? 400);
    this.chunkLimit = this.maxBatchOps;
  }

  /** Attach the window listeners that drain/replay the queue, and (re)enable the
   * model. Called from a React effect — NOT the constructor — so a discarded
   * `useMemo` instance never leaks listeners, and a StrictMode mount→unmount→remount
   * (dev) revives the model instead of leaving it permanently `disposed` (which
   * would silently stop persisting every subsequent stroke). Idempotent: the same
   * bound handlers dedupe on re-add, and a prior `dispose` removed them first. */
  attach(): void {
    this.disposed = false;
    if (typeof window !== "undefined") {
      window.addEventListener("online", this.onOnline);
      window.addEventListener("pagehide", this.onPageHide);
    }
  }

  // -- reads -----------------------------------------------------------------

  isSeeded(): boolean {
    return this.seeded;
  }

  /** Every annotation on a page (insertion order), for seeding an editor / drawing
   * a read-only overlay. Empty before the model is seeded. */
  byPage(page: number): Annotation[] {
    const ids = this.pages.get(page);
    if (!ids) return [];
    const out: Annotation[] = [];
    for (const id of ids) {
      const a = this.map.get(id);
      if (a) out.push(a);
    }
    return out;
  }

  all(): Annotation[] {
    return [...this.map.values()];
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  // -- events ----------------------------------------------------------------

  on<K extends EventKey>(event: K, fn: Handler<K>): () => void {
    this.listeners[event].add(fn);
    return () => {
      this.listeners[event].delete(fn);
    };
  }

  private emit<K extends EventKey>(event: K, ...args: InkModelEventMap[K]): void {
    for (const fn of [...this.listeners[event]]) {
      try {
        (fn as (...a: InkModelEventMap[K]) => void)(...args);
      } catch {
        /* a listener must never break the model */
      }
    }
  }

  // -- seeding + reconcile ---------------------------------------------------

  /** Populate the model from the FIRST fetch. Idempotent (only the first call
   * lands) and silent — viewers seed their editors/overlays from {@link byPage}
   * once the model reports seeded, so no events are needed here. */
  seed(annotations: Annotation[]): void {
    if (this.seeded) return;
    this.seeded = true;
    for (const a of annotations) {
      if (!this.map.has(a.id)) this.insert(cloneAnnotation(a));
    }
  }

  /** Merge a later fetch WITHOUT losing local ink. Annotations the session has
   * never seen are added (event emitted); server-known fields of an entry NOT
   * modified this session are refreshed; a locally-modified or locally-deleted
   * annotation is left as the session left it. Nothing is ever removed for being
   * absent from the server list — an unsynced local stroke has no server row yet. */
  reconcile(serverList: Annotation[]): void {
    if (!this.seeded) {
      this.seed(serverList);
      return;
    }
    for (const s of serverList) {
      if (this.tombstones.has(s.id)) continue; // locally deleted — don't resurrect
      const cur = this.map.get(s.id);
      if (cur) {
        if (this.sessionTouched.has(s.id)) continue; // local edit wins
        // Skip an UNCHANGED row (Bug B): the server stamps `updated_at` on every
        // write, so equal timestamps mean nothing changed. Emitting "replaced"
        // for every untouched stroke on every refetch is what drove the redraw
        // storm — each event redraws the whole page overlay, so an N-stroke doc
        // did O(N) redraws (each O(N)) per refetch, i.e. O(N²). This makes a
        // no-op reconcile genuinely a no-op.
        if (cur.updated_at && s.updated_at && cur.updated_at === s.updated_at) continue;
        const next = cloneAnnotation(s);
        this.map.set(s.id, next);
        this.emit("replaced", next);
      } else {
        const next = cloneAnnotation(s);
        this.insert(next);
        this.emit("added", next);
      }
    }
  }

  // -- transactions ----------------------------------------------------------

  /** Create one annotation; returns its temp id synchronously (the editor tags
   * its object with it at once, and the flush maps it to the server id). */
  create(draft: AnnotationDraft): string {
    return this.transact({ creates: [draft] })[0]!;
  }

  update(id: string, patch: Partial<AnnotationDraft>): void {
    this.transact({ updates: [{ id, patch }] });
  }

  delete(id: string): void {
    this.transact({ deletes: [id] });
  }

  /** Apply an ordered op set as ONE transaction: local state updates now, one
   * undo entry captures the inverse from the pre-state, and one batch write is
   * enqueued. Returns the temp ids of the created annotations, in order. */
  transact(ops: TxOps): string[] {
    const births = (ops.creates ?? []).map((d) => this.draftToAnnotation(d, this.nextTempId()));

    // Capture inverse payloads BEFORE applying (the pre-state).
    const mods: MarkupUndoEntry["updates"] = [];
    for (const u of ops.updates ?? []) {
      const cur = this.map.get(u.id);
      if (!cur) continue;
      const before: Partial<AnnotationDraft> = {};
      for (const k of Object.keys(u.patch)) {
        (before as Record<string, unknown>)[k] = (cur as unknown as Record<string, unknown>)[k];
      }
      mods.push({ id: u.id, before, after: { ...u.patch } });
    }
    const deaths = (ops.deletes ?? [])
      .map((id) => this.map.get(id))
      .filter((a): a is Annotation => !!a)
      .map(cloneAnnotation);

    // The editor originated this and already reflects it on its own canvas, so
    // apply WITHOUT re-emitting add/removed/replaced (that would double-render).
    this.applyLocal({ creates: births, updates: ops.updates ?? [], deletes: ops.deletes ?? [] }, false);

    if (births.length || mods.length || deaths.length) {
      this.pushEntry({ kind: "markup", creates: births.map(cloneAnnotation), updates: mods, deletes: deaths });
    }
    this.scheduleFlush();
    return births.map((b) => b.id);
  }

  // -- page ops (plan F9) ----------------------------------------------------

  /** Renumber every annotation on a page > `from` by `delta` (±1) in the map + page
   * index — the LOCAL mirror of the server's page shift. A pending create reads its
   * annotation's page at flush time, so a renumber automatically corrects the page
   * it persists to; an update never carries page (an immutable field), so it is
   * unaffected. Emits nothing: the owning viewer rebuilds the affected page editors
   * (or redraws the overlay) after a page op. */
  private shiftPages(from: number, delta: number): void {
    // Collect first (mutating the page index while iterating it is unsafe), then
    // re-home each — every moved annotation keeps its id, only its page changes.
    const moving: Annotation[] = [];
    for (const [page, ids] of this.pages) {
      if (page <= from) continue;
      for (const id of ids) {
        const a = this.map.get(id);
        if (a) moving.push(a);
      }
    }
    for (const a of moving) this.removeFromIndex(a);
    for (const a of moving) {
      a.page += delta;
      this.insert(a); // re-adds it to the (new) page set; map entry is already present
    }
  }

  /** Delete page `index` locally, mirroring the server DELETE endpoint (plan F9):
   * remove that page's annotations — returned as snapshots for the undo entry — and
   * renumber annotations on pages > index DOWN by one. The endpoint performs the
   * authoritative delete + shift atomically, so this enqueues NO batch writes for
   * them; it only brings the local model in line, drops now-obsolete queued ops for
   * the removed ink, and tombstones a server-known removed row so an in-flight
   * refetch can't resurrect it. */
  deletePageLocal(index: number): Annotation[] {
    const live = this.byPage(index); // a fresh array — safe to mutate the map under it
    const captured = live.map(cloneAnnotation);
    for (const a of live) {
      this.removeFromIndex(a);
      this.map.delete(a.id);
      this.dirtyCreates.delete(a.id);
      this.dirtyUpdates.delete(a.id);
      this.dirtyDeletes.delete(a.id);
      if (!isTempId(a.id)) this.tombstones.add(a.id);
    }
    this.shiftPages(index, -1);
    return captured;
  }

  /** Insert an empty page AFTER page `index` locally, mirroring the server insert
   * endpoint (plan F9): renumber annotations on pages > index UP by one. No ink is
   * created and no batch write is enqueued (the endpoint shifts the server rows). */
  insertPageLocal(index: number): void {
    this.shiftPages(index, +1);
  }

  /** Put annotations a FAILED page-delete had optimistically removed back into the
   * model, keeping their ORIGINAL ids and enqueuing nothing — the server rows were
   * never actually deleted (the endpoint call failed), so this only re-syncs the
   * local model. A row that had never synced (a temp id) is re-queued so it still
   * persists; the optimistic tombstone is lifted. Pair with {@link insertPageLocal}
   * to reverse the optimistic down-shift. */
  restoreLocal(annotations: Annotation[]): void {
    for (const a of annotations) {
      const clone = cloneAnnotation(a);
      if (!this.map.has(clone.id)) this.insert(clone);
      if (isTempId(clone.id)) this.dirtyCreates.add(clone.id);
      else this.tombstones.delete(clone.id);
    }
  }

  /** Re-create annotations as NEW rows (fresh temp ids) and enqueue them, WITHOUT
   * pushing an undo entry — for a page command's undo/redo closure, which is ITSELF
   * the stack entry (a nested transact would corrupt the undo/redo stacks). The
   * server rows were really deleted (the page-delete endpoint succeeded), so undoing
   * that delete must re-persist the ink as fresh rows — the plan says recoverable,
   * not id-stable. Emits nothing; the viewer reseeds the rebuilt page editors from
   * the model. Returns the new temp ids, in order. */
  recreate(snapshots: Annotation[]): string[] {
    const births = snapshots.map((s) => {
      const birth = this.draftToAnnotation(annotationToCreate(s), this.nextTempId());
      // Rebase every undo/redo entry that still targets the captured id onto the
      // fresh row (as {@link reincarnate} does). The captured ink is re-created
      // under a NEW id, so a still-stacked MARKUP entry that first created/edited/
      // deleted this stroke would otherwise point at the now-dead id — a later
      // undo/redo of it would silently no-op and strand the live row.
      for (const e of this.undoStack) this.reIdEntry(e, s.id, birth.id);
      for (const e of this.redoStack) this.reIdEntry(e, s.id, birth.id);
      return birth;
    });
    this.applyLocal({ creates: births, updates: [], deletes: [] }, false);
    this.scheduleFlush();
    return births.map((b) => b.id);
  }

  // -- undo / redo -----------------------------------------------------------

  /** Reverse the most recent transaction: restore edited fields, delete what it
   * created, re-create what it deleted (fresh ids). Applies + enqueues a batch but
   * pushes NO new undo entry; the entry moves to the redo stack. A COMMAND entry
   * (page op — plan F9) runs its own undo closure instead: it performs its endpoint
   * call + local renumber + ink re-creation, owning its errors/toasts. */
  undo(): void {
    const entry = this.undoStack.pop();
    if (!entry) return;
    if (entry.kind === "command") {
      this.redoStack.push(entry);
      this.emit("undoState");
      void entry.undo();
      return;
    }
    const updates = entry.updates.map((m) => ({ id: m.id, patch: m.before }));
    const deletes = entry.creates.map((c) => c.id);
    const creates = entry.deletes.map((d) => this.reincarnate(d));
    this.applyLocal({ creates, updates, deletes }, true);
    this.redoStack.push(entry);
    this.scheduleFlush();
    this.emit("undoState");
  }

  /** Re-apply the most recently undone transaction (fresh ids for its creates), or
   * run a command entry's redo closure. */
  redo(): void {
    const entry = this.redoStack.pop();
    if (!entry) return;
    if (entry.kind === "command") {
      this.undoStack.push(entry);
      this.emit("undoState");
      void entry.redo();
      return;
    }
    const updates = entry.updates.map((m) => ({ id: m.id, patch: m.after }));
    const deletes = entry.deletes.map((d) => d.id);
    const creates = entry.creates.map((c) => this.reincarnate(c));
    this.applyLocal({ creates, updates, deletes }, true);
    this.undoStack.push(entry);
    this.scheduleFlush();
    this.emit("undoState");
  }

  /** Mint a fresh annotation from a snapshot with a new temp id, and record that
   * id back into the snapshot so the OPPOSITE operation (a later undo/redo) targets
   * it. A re-created annotation is a NEW row server-side — its old id is gone. */
  private reincarnate(snapshot: Annotation): Annotation {
    const oldId = snapshot.id;
    const id = this.nextTempId();
    snapshot.id = id;
    // The annotation formerly known as `oldId` now lives under a fresh id. Any
    // OTHER stack entry that referenced it (e.g. the create-transaction entry when
    // this annotation was later deleted in a separate transaction) must follow, or
    // a subsequent undo/redo would target a dead id and strand the live row.
    for (const e of this.undoStack) this.reIdEntry(e, oldId, id);
    for (const e of this.redoStack) this.reIdEntry(e, oldId, id);
    return cloneAnnotation({ ...snapshot, id });
  }

  private pushEntry(entry: AnyUndoEntry): void {
    this.undoStack.push(entry);
    if (this.undoStack.length > UNDO_DEPTH) this.undoStack.shift();
    this.redoStack = []; // a fresh user op invalidates the redo future
    this.emit("undoState");
  }

  /** Record a self-contained command (page add/remove — plan F9) on the undo stack:
   * its undo/redo closures perform their own endpoint call + local renumber, so the
   * model just sequences them into the shared stack (same depth cap, redo-clear, and
   * undoState emission as a markup entry). */
  pushCommand(undo: () => void | Promise<void>, redo: () => void | Promise<void>): void {
    this.pushEntry({ kind: "command", undo, redo });
  }

  // -- local state -----------------------------------------------------------

  /** The single primitive that mutates local state + the dirty queue. `emit`
   * controls whether add/removed/replaced fire: the editor's own mutations pass
   * false (it already rendered them); undo/redo pass true so the editor reflects a
   * change it didn't originate. */
  private applyLocal(r: ResolvedOps, emit: boolean): void {
    for (const a of r.creates) {
      this.insert(a);
      this.sessionTouched.add(a.id);
      this.dirtyCreates.add(a.id);
      if (emit) this.emit("added", a);
    }
    for (const u of r.updates) {
      const cur = this.map.get(u.id);
      if (!cur) continue;
      const next: Annotation = { ...cur, ...u.patch, id: cur.id, page: cur.page, type: cur.type };
      this.map.set(cur.id, next);
      this.sessionTouched.add(cur.id);
      this.markDirtyUpdate(cur.id);
      if (emit) this.emit("replaced", next);
    }
    for (const id of r.deletes) {
      const cur = this.map.get(id);
      if (!cur) continue;
      this.removeFromIndex(cur);
      this.map.delete(id);
      this.sessionTouched.add(id);
      this.markDirtyDelete(id);
      if (emit) this.emit("removed", id, cur.page);
    }
  }

  private markDirtyUpdate(id: string): void {
    // A pending create carries the annotation's whole state (its draft is rebuilt
    // from the model at flush), so an update folds into it — nothing extra to queue.
    if (this.dirtyCreates.has(id)) return;
    this.dirtyUpdates.add(id);
  }

  private markDirtyDelete(id: string): void {
    if (this.dirtyCreates.has(id)) {
      // Never persisted — cancel the create (and any folded update) outright.
      this.dirtyCreates.delete(id);
      this.dirtyUpdates.delete(id);
      return;
    }
    if (isTempId(id)) {
      // Its create is in-flight; the server doesn't know this id yet. The flush
      // response cleans up the orphan (the local row is already gone).
      this.dirtyUpdates.delete(id);
      return;
    }
    this.dirtyUpdates.delete(id);
    this.dirtyDeletes.add(id);
    this.tombstones.add(id);
  }

  private insert(a: Annotation): void {
    this.map.set(a.id, a);
    let s = this.pages.get(a.page);
    if (!s) this.pages.set(a.page, (s = new Set()));
    s.add(a.id);
  }

  private removeFromIndex(a: Annotation): void {
    this.pages.get(a.page)?.delete(a.id);
  }

  private draftToAnnotation(d: AnnotationDraft, id: string): Annotation {
    return {
      id,
      document_id: this.docId,
      created_by: "",
      page: d.page,
      type: d.type,
      points: d.points ?? null,
      text: d.text ?? null,
      color: d.color ?? null,
      font_family: d.font_family ?? null,
      font_size_rel: d.font_size_rel ?? null,
      stroke_rel: d.stroke_rel ?? null,
      opacity: d.opacity ?? null,
      rect: d.rect ?? null,
      rings: d.rings ?? null,
      cols: d.cols ?? null,
      rows: d.rows ?? null,
      fill_color: d.fill_color ?? null,
      dash: d.dash ?? null,
      corner_radius: d.corner_radius ?? null,
    };
  }

  /** A GLOBALLY-unique temp id — not a per-session counter — because it becomes
   * the create's `ref`, which the server persists. If a batch commits but its
   * response is lost (network drop, or a pagehide beacon whose reply is unreadable)
   * the client replays the SAME batch; a globally-unique ref lets the server map
   * the replayed create back to the row it already made instead of duplicating it.
   * `tmp-<uuid>` is 40 chars, within the server's 64-char ref cap. The `tmp-`
   * prefix (isTempId) and every downstream reId/undo semantic are unchanged — only
   * the generation differs. `tempSeq` still backs the fallback for engines without
   * `crypto.randomUUID`. */
  private nextTempId(): string {
    const c = typeof crypto !== "undefined" ? crypto : undefined;
    const uuid =
      c && typeof c.randomUUID === "function"
        ? c.randomUUID()
        : `${Date.now().toString(36)}-${(++this.tempSeq).toString(36)}-${Math.random()
            .toString(36)
            .slice(2, 10)}`;
    return `tmp-${uuid}`;
  }

  /** Rewrite every reference to `oldId` (temp) as `newId` (server): the map + page
   * index, the dirty queue, and both undo stacks — so a flush's id resolution is
   * total and later edits/undos target the real row. */
  private reId(oldId: string, newId: string): void {
    const a = this.map.get(oldId);
    if (a) {
      this.map.delete(oldId);
      a.id = newId;
      this.map.set(newId, a);
      const s = this.pages.get(a.page);
      if (s) {
        s.delete(oldId);
        s.add(newId);
      }
    }
    if (this.dirtyCreates.delete(oldId)) {
      /* the create just succeeded — do NOT re-queue it */
    }
    if (this.dirtyUpdates.delete(oldId)) this.dirtyUpdates.add(newId);
    if (this.dirtyDeletes.delete(oldId)) this.dirtyDeletes.add(newId);
    if (this.sessionTouched.delete(oldId)) this.sessionTouched.add(newId);
    if (this.tombstones.delete(oldId)) this.tombstones.add(newId);
    for (const e of this.undoStack) this.reIdEntry(e, oldId, newId);
    for (const e of this.redoStack) this.reIdEntry(e, oldId, newId);
  }

  private reIdEntry(e: AnyUndoEntry, oldId: string, newId: string): void {
    // A command entry (page op) re-creates its ink with fresh ids on undo, so it
    // holds no live id to rewrite.
    if (e.kind !== "markup") return;
    for (const c of e.creates) if (c.id === oldId) c.id = newId;
    for (const u of e.updates) if (u.id === oldId) u.id = newId;
    for (const d of e.deletes) if (d.id === oldId) d.id = newId;
  }

  // -- persistence -----------------------------------------------------------

  private hasDirty(): boolean {
    return this.dirtyCreates.size > 0 || this.dirtyUpdates.size > 0 || this.dirtyDeletes.size > 0;
  }

  private scheduleFlush(): void {
    if (this.disposed || this.flushTimer !== null) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      void this.flush();
    }, this.flushDelayMs);
  }

  private scheduleReplay(): void {
    if (this.disposed || this.replayTimer !== null) return;
    this.replayTimer = setTimeout(() => {
      this.replayTimer = null;
      void this.flush();
    }, this.replayDelayMs);
  }

  /** Build ONE bounded batch (<= chunkLimit ops) from the current dirty sets +
   * model state, send it, and resolve ids. Only one flush is ever in flight; ops
   * that accumulate during a send — and any dirty left over past the chunk — flush
   * on completion. Failure handling is the heart of the data-loss fix (Bug A):
   *
   *  - TRANSIENT (offline / cold auth / 5xx): re-queue and replay. Never a loss.
   *  - DEFINITIVE 4xx on a MULTI-op batch: NOT proof every op is bad — it's almost
   *    always the aggregate (too many ops / too large a body). Re-queue everything
   *    and HALVE the chunk size so the retry sends smaller batches, bisecting
   *    toward (and isolating) any single genuinely-poison op instead of dropping
   *    innocent strokes with it. Still never a loss.
   *  - DEFINITIVE 4xx on a SINGLE-op batch: that lone op is genuinely unrecoverable
   *    (a malformed stroke that will always 4xx). Drop just IT — never its
   *    neighbours — surface an honest error, and keep draining.
   *
   * Combined with proactive chunking (chunkLimit <= server cap), the old failure
   * mode where a long session's over-limit batch was rejected and DROPPED WHOLE
   * simply cannot happen: no acknowledged stroke is ever silently lost. */
  async flush(): Promise<void> {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.flushing) return; // a send is in progress; it reschedules if dirty remains
    const built = this.buildBatch(this.chunkLimit);
    if (!built) return;
    const { req, consumed } = built;

    // Remove exactly the ops this chunk carries from the dirty sets (new ops, and
    // any dirty beyond the chunk, accumulate separately). A failure restores them
    // so the replay re-derives their LATEST state.
    for (const id of consumed.creates) this.dirtyCreates.delete(id);
    for (const id of consumed.updates) this.dirtyUpdates.delete(id);
    for (const id of consumed.deletes) this.dirtyDeletes.delete(id);
    const opCount = consumed.creates.length + consumed.updates.length + consumed.deletes.length;
    this.flushing = true;

    const requeue = () => {
      for (const id of consumed.creates) if (this.map.has(id)) this.dirtyCreates.add(id);
      for (const id of consumed.updates) this.dirtyUpdates.add(id);
      for (const id of consumed.deletes) this.dirtyDeletes.add(id);
    };

    // Track the send so {@link settle} can await an already-in-flight flush (the
    // `this.flushing` guard makes a re-entrant flush() a no-op, so awaiting its
    // return isn't enough — settle awaits `inFlight` instead).
    this.inFlight = (async () => {
      try {
        const res = await this.sendBatch(req);
        this.applyBatchResult(res);
        this.parked = false;
        this.flushing = false;
        this.chunkLimit = this.maxBatchOps; // a good send re-opens the full window
        if (this.hasDirty()) this.scheduleFlush();
      } catch (e) {
        this.flushing = false;
        if (this.isTransient(e)) {
          requeue();
          this.parked = true;
          this.onPersistTrouble?.();
          this.scheduleReplay();
        } else if (opCount > 1) {
          // Definitive rejection of a multi-op batch → bisect and retry smaller.
          // No data dropped: everything is re-queued at a lower chunk size.
          requeue();
          this.chunkLimit = Math.max(1, Math.floor(opCount / 2));
          this.scheduleFlush();
        } else {
          // A single op the server definitively rejects is genuinely unrecoverable.
          // Drop only it, remove any orphaned create so the canvas doesn't keep
          // showing an unsaved stroke as if it were saved, tell the user honestly,
          // reopen the chunk window, and let unrelated pending work still flush.
          const orphans = consumed.creates.filter((id) => this.map.has(id));
          if (orphans.length) this.applyLocal({ creates: [], updates: [], deletes: orphans }, true);
          this.chunkLimit = this.maxBatchOps;
          this.onPersistReject?.(e);
          if (this.hasDirty()) this.scheduleFlush();
        }
      }
    })();
    await this.inFlight;
    this.inFlight = null;
  }

  /** Resolve once the queue is fully drained to the server — no dirty ops AND no
   * in-flight send. A page op (delete/insert) awaits this BEFORE calling its shift
   * endpoint so no create still carrying a PRE-shift page number can be in flight
   * when the server renumbers: were such a create to land after the shift it would
   * persist at a stale page — ink stranded on the wrong (or a since-removed) page.
   * Bounded, and it stops on a parked queue (offline / a definitive reject) so a
   * page op is never wedged behind an unreachable server: the optimistic local
   * shift + rollback-on-endpoint-failure still runs, and any parked create replays
   * later rebuilt from the already-shifted model at its corrected page. */
  async settle(): Promise<void> {
    for (let i = 0; i < 100; i++) {
      if (this.inFlight) {
        await this.inFlight.catch(() => {});
        continue;
      }
      if (this.parked || !this.hasDirty()) return;
      await this.flush();
    }
  }

  /** Build ONE batch of at most `limit` ops from the current dirty sets + model
   * state, returning it alongside the ids it consumed (so a failed send can
   * restore exactly them). Stale entries — a create whose row is gone, an update
   * on a deleted/temp id — are pruned here (they carry no data to lose) and don't
   * count toward the limit. Ops beyond the limit stay queued for the next flush.
   * Does NOT mutate the dirty sets for LIVE consumed ids — the caller removes
   * those after it decides to send. */
  private buildBatch(limit = Infinity): { req: MarkupBatchRequest; consumed: Consumed } | null {
    const creates: MarkupBatchCreate[] = [];
    const consumed: Consumed = { creates: [], updates: [], deletes: [] };
    let total = 0;
    for (const t of [...this.dirtyCreates]) {
      if (total >= limit) break;
      const a = this.map.get(t);
      if (!a) {
        this.dirtyCreates.delete(t); // deleted before it flushed — live guard drops it
        continue;
      }
      creates.push({ ref: t, ...annotationToCreate(a) });
      consumed.creates.push(t);
      total++;
    }
    const updates: MarkupBatchUpdate[] = [];
    for (const id of [...this.dirtyUpdates]) {
      if (total >= limit) break;
      if (isTempId(id)) continue; // its create is in-flight; wait for idChanged to rewrite it
      if (this.dirtyDeletes.has(id)) {
        this.dirtyUpdates.delete(id);
        continue;
      }
      const a = this.map.get(id);
      if (!a) {
        this.dirtyUpdates.delete(id);
        continue;
      }
      updates.push({ id, ...annotationToUpdate(a) });
      consumed.updates.push(id);
      total++;
    }
    const deletes: string[] = [];
    for (const id of [...this.dirtyDeletes]) {
      if (total >= limit) break;
      deletes.push(id);
      consumed.deletes.push(id);
      total++;
    }
    if (total === 0) return null;
    return { req: { creates, updates, deletes }, consumed };
  }

  /** Snapshot ALL currently-pending ops into independent batches of at most
   * `limit` ops each, WITHOUT mutating the dirty sets — the unload beacon path
   * must leave them intact for a possible bfcache replay, and a long parked
   * backlog must not go out as one over-limit body the server would reject (an
   * unrecoverable loss on unload). Each batch is idempotent (stable refs), so
   * several beacons + a later replay never double-write. Mirrors buildBatch's op
   * selection; skips temp-id updates and stale rows. */
  private pendingBatches(limit: number): MarkupBatchRequest[] {
    type Op =
      | { kind: "c"; v: MarkupBatchCreate }
      | { kind: "u"; v: MarkupBatchUpdate }
      | { kind: "d"; v: string };
    const ops: Op[] = [];
    for (const t of [...this.dirtyCreates]) {
      const a = this.map.get(t);
      if (a) ops.push({ kind: "c", v: { ref: t, ...annotationToCreate(a) } });
    }
    for (const id of [...this.dirtyUpdates]) {
      if (isTempId(id) || this.dirtyDeletes.has(id)) continue;
      const a = this.map.get(id);
      if (a) ops.push({ kind: "u", v: { id, ...annotationToUpdate(a) } });
    }
    for (const id of [...this.dirtyDeletes]) ops.push({ kind: "d", v: id });

    const out: MarkupBatchRequest[] = [];
    const step = Math.max(1, limit);
    for (let i = 0; i < ops.length; i += step) {
      const req: MarkupBatchRequest = { creates: [], updates: [], deletes: [] };
      for (const op of ops.slice(i, i + step)) {
        if (op.kind === "c") req.creates.push(op.v);
        else if (op.kind === "u") req.updates.push(op.v);
        else req.deletes.push(op.v);
      }
      out.push(req);
    }
    return out;
  }

  private applyBatchResult(res: MarkupBatchResult): void {
    for (const c of res.created) {
      const serverId = c.annotation.id;
      if (this.map.has(c.ref)) {
        const page = this.map.get(c.ref)!.page;
        this.reId(c.ref, serverId);
        this.emit("idChanged", c.ref, serverId, page);
      } else {
        // The local row was deleted while its create was in flight → the server
        // now holds an orphan. Queue its delete (the next flush reaps it).
        this.dirtyDeletes.add(serverId);
        this.tombstones.add(serverId);
      }
    }
  }

  /** Drain the CURRENT pending batch via `navigator.sendBeacon` — the unload-safe
   * path. A normal async {@link flush} is aborted when the tab is closing/frozen,
   * losing the last ~flush-cycle of strokes; a beacon is a fire-and-forget POST the
   * browser completes even after the page is gone. Same-origin, so the id-token
   * cookie authenticates it (a beacon can't set an Authorization header, but the
   * server accepts cookie auth for raw browser requests — the house pattern).
   *
   * Returns whether the browser queued the beacon (false if unsupported / nothing
   * pending), so a caller can fall back.
   *
   * We deliberately do NOT clear the dirty sets: a beacon's response is unreadable,
   * so we can't resolve temp ids -> server ids from it. If the page truly dies,
   * nothing else runs and the strokes are already safe server-side. If the page is
   * instead restored from bfcache, the model is still alive with its dirty sets and
   * the normal flush replays the SAME batch — which is HARMLESS: every create
   * carries a stable, globally-unique `ref`, and the server maps a duplicate ref
   * back to the row it already created (see AnnotationService.batch idempotency)
   * rather than duplicating the stroke. So beacon + replay never double-writes.
   *
   * The pending work is chunked into per-op-cap beacons (Bug A): a long parked
   * backlog must never be sent as one over-limit body the server would reject —
   * on unload there's no retry, so that would be an unrecoverable loss. Returns
   * true if the browser queued at least one beacon. */
  flushBeacon(): boolean {
    if (
      !this.beaconUrl ||
      typeof navigator === "undefined" ||
      typeof navigator.sendBeacon !== "function"
    ) {
      return false;
    }
    const batches = this.pendingBatches(this.maxBatchOps); // leaves the dirty sets intact
    if (!batches.length) return false;
    let queued = false;
    for (const req of batches) {
      const blob = new Blob([JSON.stringify(req)], { type: "application/json" });
      if (navigator.sendBeacon(this.beaconUrl, blob)) queued = true;
    }
    return queued;
  }

  /** Force a pending write out now and release listeners. Called by ReaderTab when
   * the document changes / the tab unmounts — the model outlives every editor, so
   * this is the only teardown that ends the session's persistence. */
  dispose(): void {
    this.disposed = true;
    if (this.flushTimer !== null) clearTimeout(this.flushTimer);
    if (this.replayTimer !== null) clearTimeout(this.replayTimer);
    this.flushTimer = null;
    this.replayTimer = null;
    if (typeof window !== "undefined") {
      window.removeEventListener("online", this.onOnline);
      window.removeEventListener("pagehide", this.onPageHide);
    }
    void this.flush(); // best-effort final drain
  }
}
